<template>
   <transition name="fade">
      <div 
        v-show="visible" 
        :class="[
           'alert ui-alert',
           `alert-${computedType}`,
           {
               'ui-alert--small': size === 'small',
               'ui-alert--full-width': fullWidth,
               'alert-dismissible': dismissible,
               fade: dismissible,
               show: dismissible && visible,
               'Alert-top': isLegacyAlertTop,
               'alert-promo': isLegacyAlertPromo,
               'center-top': isPositionTopCenter,
           }
        ]"
        role="alert"
       >
           <div :class="['alert__content', { 'container': fullWidth, 'alert__content--small': size === 'small' }]">
                <div v-if="$slots.badge">
                    <slot name="badge"></slot>
                </div>

                <div v-if="$slots.icon || !noIcon" class="alert__icon">
                    <slot name="icon">
                        <component v-if="iconComponent" :is="iconComponent" />
                    </slot>
                </div>

               <div class="alert__title-wrapper">
                   <div v-if="$slots.title || title" class="alert__title">
                       <slot name="title">
                           <div v-if="title" v-html="title"></div>
                       </slot>
                   </div>
                   <div v-if="$slots.description || description" :class="['alert__description', { 'small': smallDescription }]">
                       <slot name="description">
                           <div v-if="description" v-html="description"></div>
                       </slot>
                   </div>
               </div>
           </div>
           <button v-if="dismissible" type="button" class="close" aria-label="Close" @click="closeAlert">
               <svg-alert-close />
           </button>
       </div>
   </transition>
</template>

<script>
// import Translations from "./insert-me-to-translate.vue";

const icons = {
    success: "SvgAlertSuccess",
    info: "SvgAlertInfo",
    error: "SvgAlertError",
    warning: "SvgAlertWarning",
};

export default {
    components: {
        SvgAlertClose: () => import(/* webpackChunkName: "chunk-svg-alert-close" */ '../svg/alert-close.vue'),
        SvgAlertSuccess: () => import(/* webpackChunkName: "chunk-svg-alert-success" */ '../svg/alert-success.vue'),
        SvgAlertInfo: () => import(/* webpackChunkName: "chunk-svg-alert-info" */ '../svg/alert-info.vue'),
        SvgAlertError: () => import(/* webpackChunkName: "chunk-svg-alert-error" */ '../svg/alert-error.vue'),
        SvgAlertWarning: () => import(/* webpackChunkName: "chunk-svg-alert-warning" */ '../svg/alert-warning.vue'),
    },
    props: {
        type: {
            type: String,
            default: 'info',
            validator(value) {
                return ['success','error','warning','info','debug','blank'].includes(value);
            }
        },
        dismissible: {
            type: Boolean,
            default: false
        },
        initialVisible: {
            type: Boolean,
            default: true
        },
        title: {
            type: String,
            default: null,
        },
        description: {
            type: String,
            default: null,
        },
        noIcon: {
            type: Boolean,
            default: false
        },
        autodismiss: {
            type: Boolean,
            default: false,
        },
        autodismissDelay: {
            type: Number,
            default: 10000,
        },
        size: {
            type: String,
            default: 'default',
            validator(value) {
                return ['default','small'].includes(value);
            }
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        legacy: {
            type: String,
            default: null,
            validator(value) {
                return ['alert-top', 'alert-promo'].includes(value);
            }
        },
        position: {
            type: String,
            default: null,
            validator(value) {
                return ['top-center'].includes(value);
            }
        },
        smallDescription: Boolean,
    },
    data() {
        return {
            visible: this.initialVisible,
            timeoutId: null,
        };
    },
    mounted() {
        if (this.visible && this.autodismiss) {
            this.startAutoDismissTimer();
        }
    },
    beforeDestroy() {
        if(this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    },
    watch: {
        visible(newValue) {
            if (newValue) {
                if(this.autodismiss) {
                    this.startAutoDismissTimer();
                }
            }
        }
    },
    computed: {
        iconComponent() {
            return icons[this.type] ? icons[this.type] : null;
        },
        isLegacyAlertTop() {
            return this.legacy === 'alert-top';
        },
        isLegacyAlertPromo() {
            return this.legacy === 'alert-promo';
        },
      isPositionTopCenter() {
          return this.position === 'top-center';
      },
        computedType() {
            switch(this.type) {
                case 'debug':
                    return 'info';
                case 'success':
                    return 'success';
                case 'info':
                    return 'info';
                case 'warning':
                    return 'warning';
                case 'error':
                    return 'error';
                case 'blank':
                    return 'blank';
                default:
                    return 'info';
            }
        }
    },
    methods: {
        closeAlert() {
            this.visible = false;
            clearTimeout(this.timeoutId);
            this.$emit('close');
        },
        startAutoDismissTimer() {
            this.timeoutId = setTimeout(() => {
                this.visible = false;
                this.$emit('close');
            }, this.autodismissDelay);
        },
    },
}
</script>


<style>
.ui-alert {
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    gap: 16px 0;
    border: 0; /* Unified border for all alerts */
    color: var(--color-alert);
    transition: all .3s ease-in-out;
}
.ui-alert .alert__content {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
}
.ui-alert .alert__content svg {
    height: auto;
}

.ui-alert.alert-dismissible .close {
    position: initial;
    padding: 8px;
}
.ui-alert .alert__title-wrapper {
    flex: 1;
}
.ui-alert .alert__title {
    font-size: 14px;
    font-weight: 700;
    color: var(--color-message);
}
.ui-alert .alert__description {
    font-size: 14px;
    font-weight: 400;
    color: var(--color-message);
}
.ui-alert .alert__description a {
    color: var(--color-primary);
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    text-decoration: underline !important;
}
.ui-alert .alert__description a.ui-button {
    text-decoration: none !important;
}

/* Small */

.ui-alert.ui-alert--small {
    padding: 10px 8px;
    border-radius: 4px;
}
.ui-alert .alert__content--small {
    gap: 8px;
}
.ui-alert.ui-alert--small .alert__title,
.ui-alert.ui-alert--small .alert__title a {
    font-size: var(--font-size-small-medium);
    text-transform: uppercase;
}
.ui-alert.ui-alert--small .alert__description,
.ui-alert.ui-alert--small .alert__description a {
    font-size: var(--font-size-small);
}
.ui-alert.ui-alert--small .icon svg {
    width: 22px;
}

/* Alert Colors */
.ui-alert.alert-success {
    background-color: var(--background-alert-success);
}
.ui-alert.alert-info {
    background-color: var(--background-alert-info);
}
.ui-alert.alert-warning {
    background-color: var(--background-alert-warning);
}
.ui-alert.alert-error {
    background-color: var(--background-alert-error);
}

.fade-leave-active {
    transition: opacity .2s ease;
}
.fade-leave-to {
    opacity: 0;
}

.ui-alert.Alert-top {
  position: fixed;
  top: 48px;
  left: 7%;
  width: 86%;
  z-index: 1021;
}
.ui-alert.alert-promo {
  background-color: var(--background-alert-promo) !important;
  border-color: var(--color-alert-promo) !important;
}

.ui-alert .fa {
    font-size: 20px;
}
.ui-alert.alert-info .fa:before {
    color: var(--color-alert-info);
}
.ui-alert.alert-warning .fa:before {
    color: var(--color-alert-warning);
}
.ui-alert.alert-error .fa:before {
    color: var(--color-alert-error);
}
.ui-alert.alert-success .fa:before {
    color: var(--color-alert-success);
}

.ui-alert.ui-alert--full-width {
    border-radius: 0;
}

.ui-alert.center-top {
  max-width: 354px;
  position: fixed;
  top: 20px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  z-index: 1000;
}
.ui-alert.alert-blank {
    border-radius: 12px;
    border: 1px solid var(--color-gray-15);
    background: var(--color-white);
}
.ui-alert.alert-blank .alert__title {
    color: var(--color-text-9);
    text-transform: initial;
}
.ui-alert.alert-blank .alert__icon svg,
.ui-alert.alert-blank.ui-alert--small .alert__icon svg {
    width: 40px;
    height: auto;
}
.ui-alert.alert-blank .alert__description {
    color: var(--color-gray-6);
}

</style>
